import type { Dispatch, SetStateAction } from "react";
import type {
  GhlAvailPipeline,
  GhlConnection,
  GhlNewPipelineDraft,
  GhlPipeline,
  LocationOption,
} from "./ghlSectionShared";
import { fmtDate } from "./ghlSectionShared";
import { GhlConnectionAddPipelineForm } from "./GhlConnectionAddPipelineForm";

export type GhlConnectionCardProps = {
  conn: GhlConnection;
  pipes: GhlPipeline[];
  collapsed: boolean;
  onToggleCollapse: () => void;
  locationLabel: (locationId: string | null) => string | null;
  loading: boolean;
  editWebhookOpen: boolean;
  editWebhookValue: string;
  onEditWebhookToggle: () => void;
  onEditWebhookValueChange: (value: string) => void;
  onSaveWebhook: () => void;
  onCancelWebhook: () => void;
  editKeyOpen: boolean;
  editKeyValue: string;
  onEditKeyToggle: () => void;
  onEditKeyValueChange: (value: string) => void;
  onSaveKey: () => void;
  onCancelKey: () => void;
  onToggleConnection: () => void;
  onTogglePipeline: (pipelineId: string, isActive: boolean, pipelineName?: string) => void;
  showAddPipe: boolean;
  newPipe: GhlNewPipelineDraft;
  setNewPipe: Dispatch<SetStateAction<GhlNewPipelineDraft>>;
  loadingPipes: boolean;
  availPipelines: GhlAvailPipeline[];
  knownProductTypes: string[];
  locations: LocationOption[];
  autofillMsg: { text: string; ok: boolean } | null;
  onOpenAddPipeline: () => void;
  onAddPipeline: () => void;
  onCancelAddPipeline: () => void;
  tryAutofillFromName: (pipelineName: string) => void;
  /** When false, pipeline Active/Inactive is read-only (org admin / dev / platform may toggle). */
  canTogglePipelineActive?: boolean;
};

export function GhlConnectionCard({
  conn,
  pipes,
  collapsed,
  onToggleCollapse,
  locationLabel,
  loading,
  editWebhookOpen,
  editWebhookValue,
  onEditWebhookToggle,
  onEditWebhookValueChange,
  onSaveWebhook,
  onCancelWebhook,
  editKeyOpen,
  editKeyValue,
  onEditKeyToggle,
  onEditKeyValueChange,
  onSaveKey,
  onCancelKey,
  onToggleConnection,
  onTogglePipeline,
  showAddPipe,
  newPipe,
  setNewPipe,
  loadingPipes,
  availPipelines,
  knownProductTypes,
  locations,
  autofillMsg,
  onOpenAddPipeline,
  onAddPipeline,
  onCancelAddPipeline,
  tryAutofillFromName,
  canTogglePipelineActive = true,
}: GhlConnectionCardProps) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
        <div>
          <div className="font-medium text-sm">{conn.company_name}</div>
          <div className="text-xs text-gray-500 mt-0.5 font-mono">{conn.ghl_location_id}</div>
          <div className="text-xs text-gray-400 mt-0.5">Last synced: {fmtDate(conn.last_synced_at)}</div>
          {conn.webhook_url ? (
            <div className="text-[10px] text-emerald-600 mt-0.5">✓ Webhook configured</div>
          ) : (
            <div className="text-[10px] text-amber-600 mt-0.5">⚠ No webhook — using env fallback</div>
          )}
        </div>
        <button
          type="button"
          onClick={() => void onToggleConnection()}
          disabled={loading}
          className={`text-xs px-2 py-1 rounded-full font-medium ${
            conn.is_active
              ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
              : "bg-gray-100 text-gray-500 hover:bg-gray-200"
          }`}
        >
          {conn.is_active ? "Active" : "Inactive"}
        </button>
        <button
          type="button"
          onClick={onEditKeyToggle}
          className="text-xs px-2 py-1 rounded-full font-medium bg-gray-100 text-gray-500 hover:bg-gray-200"
          title="Update stored API key (required after regenerating a GHL Private Integration key)"
        >
          Update key
        </button>
        <button
          type="button"
          onClick={onEditWebhookToggle}
          className="text-xs px-2 py-1 rounded-full font-medium bg-gray-100 text-gray-500 hover:bg-gray-200"
          title="Set or change the GHL webhook URL for this connection"
        >
          {conn.webhook_url ? "Edit webhook" : "Set webhook"}
        </button>
      </div>
      {editWebhookOpen && (
        <div className="px-4 py-2 bg-blue-50 border-t border-blue-100 flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <input
              type="url"
              className="flex-1 text-xs border border-blue-200 rounded-lg px-2 py-1.5 font-mono bg-white"
              placeholder="https://services.leadconnectorhq.com/hooks/…"
              value={editWebhookValue}
              onChange={(e) => onEditWebhookValueChange(e.target.value)}
              autoComplete="off"
            />
            <button
              type="button"
              onClick={() => void onSaveWebhook()}
              disabled={loading}
              className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
            >
              Save
            </button>
            <button
              type="button"
              onClick={onCancelWebhook}
              className="text-xs px-2 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
          <p className="text-[10px] text-blue-600 px-0.5">Leave blank and save to clear the webhook (falls back to env var).</p>
        </div>
      )}
      {editKeyOpen && (
        <div className="px-4 py-2 bg-amber-50 border-t border-amber-100 flex items-center gap-2">
          <input
            type="password"
            className="flex-1 text-xs border border-amber-200 rounded-lg px-2 py-1.5 font-mono bg-white"
            placeholder="Paste new GHL API key…"
            value={editKeyValue}
            onChange={(e) => onEditKeyValueChange(e.target.value)}
            autoComplete="off"
          />
          <button
            type="button"
            onClick={() => void onSaveKey()}
            disabled={loading || !editKeyValue.trim()}
            className="text-xs px-3 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 whitespace-nowrap"
          >
            Save key
          </button>
          <button
            type="button"
            onClick={onCancelKey}
            className="text-xs px-2 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      )}
      <div className="divide-y divide-gray-100">
        {pipes.length > 0 && (
          <button
            type="button"
            onClick={onToggleCollapse}
            className="w-full flex items-center justify-between px-4 py-2 text-xs text-gray-500 hover:bg-gray-50 font-medium"
          >
            <span>
              {pipes.length} pipeline{pipes.length !== 1 ? "s" : ""}
            </span>
            <span>{collapsed ? "▸ Show" : "▾ Hide"}</span>
          </button>
        )}
        {pipes.length === 0 && (
          <div className="px-4 py-3 text-xs text-gray-400 italic">No pipelines added yet.</div>
        )}
        {!collapsed &&
          pipes.map((pipe) => (
            <div key={pipe.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
              <div>
                <span className="font-medium">{pipe.pipeline_name}</span>
                <span className="text-xs text-gray-400 font-mono ml-2">{pipe.ghl_pipeline_id}</span>
                {pipe.product_type && (
                  <span className="ml-2 text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded-full">
                    {pipe.product_type}
                  </span>
                )}
                {pipe.location_id && (
                  <span className="ml-2 text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full">
                    {locationLabel(pipe.location_id)}
                  </span>
                )}
                {!pipe.location_id && (
                  <span className="ml-2 text-xs bg-gray-50 text-gray-400 px-1.5 py-0.5 rounded-full">org-wide</span>
                )}
              </div>
              {canTogglePipelineActive ? (
                <button
                  type="button"
                  onClick={() => void onTogglePipeline(pipe.id, pipe.is_active, pipe.pipeline_name)}
                  disabled={loading}
                  className={`text-xs px-2 py-1 rounded-full font-medium ${
                    pipe.is_active
                      ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                      : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                  }`}
                >
                  {pipe.is_active ? "Active" : "Inactive"}
                </button>
              ) : (
                <span
                  title="Only org admins, owners, dev, or platform staff can activate or deactivate pipelines."
                  className={`text-xs px-2 py-1 rounded-full font-medium cursor-default ${
                    pipe.is_active ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-gray-400"
                  }`}
                >
                  {pipe.is_active ? "Active" : "Inactive"}
                </span>
              )}
            </div>
          ))}

        <GhlConnectionAddPipelineForm
          conn={conn}
          showAddPipe={showAddPipe}
          newPipe={newPipe}
          setNewPipe={setNewPipe}
          loading={loading}
          loadingPipes={loadingPipes}
          availPipelines={availPipelines}
          knownProductTypes={knownProductTypes}
          locations={locations}
          autofillMsg={autofillMsg}
          onOpenAddPipeline={onOpenAddPipeline}
          onAddPipeline={onAddPipeline}
          onCancelAddPipeline={onCancelAddPipeline}
          tryAutofillFromName={tryAutofillFromName}
        />
      </div>
    </div>
  );
}
